import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { accrualsApi, type PostScope, type PostSource } from './api'
import type { AccrualPayload } from './types'

export function useAccruals() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'accruals'),
    queryFn: accrualsApi.list,
  })
}

export function usePostingStatus() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'posting-status'),
    queryFn: accrualsApi.postingStatus,
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>, tenantId: string | null) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'accruals') })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'posting-status') })
}

export function useSaveAccruals() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (rows: AccrualPayload[]) => accrualsApi.saveAll(rows),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

export function useDeleteAccrual() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => accrualsApi.remove(id),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

/**
 * Post unposted GL transactions. Pass `scope` to restrict to a single
 * source (e.g. 'depreciation' from the Fixed Assets page so clicking
 * "Post now" there doesn't also post unposted bank transactions).
 * Defaults to 'all' for the main Reporting page.
 */
export function usePostAccruals(scope: PostScope = 'all') {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: () => accrualsApi.postTransactions(scope),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

/**
 * Post a user-selected subset of one source's unposted entries.
 *
 * Each "Post Now" banner picks rows via <PostingSelectorModal>; the
 * modal hands back `{ source, ids[] }` and we fan out one mutation
 * per source. Posting one source NEVER triggers another's queue -
 * that's enforced server-side in /api/reporting/post-selected.
 */
export function usePostSelected() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ source, ids }: { source: PostSource; ids: number[] }) =>
      accrualsApi.postSelected(source, ids),
    onSuccess: () => {
      invalidate(qc, tenantId)
      // Invalidate every cache key the post-now surfaces read from so
      // the banner count, the modal preview, and any per-source page
      // (Invoices / Bills / Accruals / etc.) all show fresh state after
      // a post. The two `posting-status` keys exist because reporting
      // and accruals features use independent query namespaces - both
      // need to clear or the Reports banner stays stale.
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'reporting') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'ap') })
    },
  })
}
